import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { quotesApi, clientsApi, productsApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import ProductSearchDialog from '../components/ProductSearchDialog';
import ClientSearch from '../components/ClientSearch';
import { Trash2, Save, ArrowLeft, PackageSearch, ArrowRightLeft, Printer } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Form unificato per creazione/modifica Preventivi.
 * Modalità edit determinata dalla presenza di useParams().id
 */
const QuoteFormPage = () => {
  const navigate = useNavigate();
  const { id: quoteId } = useParams();
  const isEdit = Boolean(quoteId);

  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [readOnly, setReadOnly] = useState(false);

  const [selectedClientId, setSelectedClientId] = useState('');
  const [items, setItems] = useState([]);
  const [listino, setListino] = useState('1');
  const [validUntil, setValidUntil] = useState('');
  const [title, setTitle] = useState('');
  const [quote, setQuote] = useState(null);
  const [formData, setFormData] = useState({
    causale: 'Vendita', aspetto_beni: 'A VISTA', porto: 'Franco',
    transport_date: new Date().toISOString().split('T')[0],
    transport_time: '', vettore: '', note: '', internal_ref: '',
  });

  useEffect(() => { bootstrap(); }, []); // eslint-disable-line

  const bootstrap = async () => {
    try {
      const [c, p] = await Promise.all([clientsApi.getAll(), productsApi.getAll()]);
      setClients(c.data); setProducts(p.data);
      if (isEdit) {
        const q = await quotesApi.getById(quoteId);
        const qd = q.data;
        setQuote(qd);
        setSelectedClientId(qd.client_id || '');
        setListino(qd.listino || '1');
        setItems(qd.items || []);
        setValidUntil(qd.valid_until || '');
        setTitle(qd.title || '');
        setFormData({
          causale: qd.causale || 'Vendita',
          aspetto_beni: qd.aspetto_beni || 'A VISTA',
          porto: qd.porto || 'Franco',
          transport_date: qd.transport_date || new Date().toISOString().split('T')[0],
          transport_time: qd.transport_time || '',
          vettore: qd.vettore || '',
          note: qd.note || '',
          internal_ref: qd.internal_ref || '',
        });
        if (qd.quote_status === 'converted') {
          setReadOnly(true);
          toast.info('Preventivo già convertito in DDT — modalità sola lettura');
        }
      }
    } catch (e) { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  };

  const selectedClient = clients.find(c => c.id === selectedClientId);
  const priceForListino = (p, l) => l === '2' ? (p.price_2 || p.price || 0) : l === '3' ? (p.price_3 || p.price || 0) : (p.price || 0);

  const addProduct = (product, boxes, explicitPieces) => {
    const b = Math.round((parseFloat(boxes) || 0) * 100) / 100;
    if (b <= 0) return;
    const ppb = parseInt(product.pieces_per_box) || 1;
    const pieces = (explicitPieces != null)
      ? Math.round(parseFloat(explicitPieces) || 0)
      : Math.round(b * ppb);
    const price = priceForListino(product, listino);
    const total = pieces * price;
    const total_cost = pieces * (product.cost || 0);
    setItems(prev => [...prev, {
      product_id: product.id, product_code: product.code, description: product.description,
      boxes: b, pieces_per_box: ppb, pieces, price, cost: product.cost || 0,
      iva: product.iva || 22, total, total_cost, margin: total - total_cost,
    }]);
    toast.success(`${product.code} aggiunto`, { duration: 1500 });
  };

  const updateItem = (i, field, value) => {
    const ni = [...items]; ni[i][field] = value;
    const it = ni[i];
    const ppb = parseFloat(it.pieces_per_box) || 1;
    const b = parseFloat(it.boxes) || 0;
    const px = parseFloat(it.pieces) || 0;
    if (field === 'boxes') it.pieces = Math.round(b * ppb);
    else if (field === 'pieces') {
      it.pieces = Math.round(px);
      it.boxes = ppb > 0 ? Math.round((it.pieces / ppb) * 100) / 100 : 0;
    }
    else if (field === 'pieces_per_box') it.pieces = Math.round(b * ppb);
    it.total = (parseFloat(it.pieces) || 0) * (parseFloat(it.price) || 0);
    it.total_cost = (parseFloat(it.pieces) || 0) * (parseFloat(it.cost) || 0);
    it.margin = it.total - it.total_cost;
    setItems(ni);
  };

  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const totals = {
    total_boxes: items.reduce((s, i) => s + (parseFloat(i.boxes) || 0), 0),
    total_pieces: items.reduce((s, i) => s + (parseFloat(i.pieces) || 0), 0),
    total_amount: items.reduce((s, i) => s + (parseFloat(i.total) || 0), 0),
    total_cost: items.reduce((s, i) => s + (parseFloat(i.total_cost) || 0), 0),
  };
  totals.total_margin = totals.total_amount - totals.total_cost;
  const fmt = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);

  const buildPayload = () => ({
    client_id: selectedClient?.id || '',
    client_name: selectedClient ? ((selectedClient.nickname?.trim()) || selectedClient.name) : '',
    client_legal_name: selectedClient?.name || '',
    client_address: selectedClient?.address || '',
    client_city: selectedClient?.city || '',
    client_cap: selectedClient?.cap || '',
    client_piva: selectedClient?.piva || '',
    items: items.map(it => ({
      product_id: it.product_id, product_code: it.product_code, description: it.description,
      boxes: parseFloat(it.boxes) || 0, pieces_per_box: parseInt(it.pieces_per_box) || 0,
      pieces: parseFloat(it.pieces) || 0, price: parseFloat(it.price) || 0,
      cost: parseFloat(it.cost) || 0, iva: parseFloat(it.iva) || 22,
      total: parseFloat(it.total) || 0, total_cost: parseFloat(it.total_cost) || 0,
      margin: parseFloat(it.margin) || 0,
    })),
    total_boxes: totals.total_boxes, total_pieces: totals.total_pieces,
    total_amount: totals.total_amount, total_cost: totals.total_cost, total_margin: totals.total_margin,
    paid_amount: 0,
    causale: formData.causale, aspetto_beni: formData.aspetto_beni, porto: formData.porto,
    transport_date: formData.transport_date, transport_time: formData.transport_time,
    vettore: formData.vettore, note: formData.note, internal_ref: formData.internal_ref,
    listino, valid_until: validUntil, title,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedClient) { toast.error('Seleziona un cliente'); return; }
    if (items.length === 0) { toast.error('Aggiungi almeno un prodotto'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await quotesApi.update(quoteId, buildPayload());
        toast.success('Preventivo aggiornato');
      } else {
        const r = await quotesApi.create(buildPayload());
        toast.success(`Preventivo PR-${r.data.number}/${r.data.year} creato`);
      }
      navigate('/preventivi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore salvataggio');
    } finally { setSaving(false); }
  };

  const handleConvert = async () => {
    if (!window.confirm('Convertire questo preventivo in DDT? Verrà generato un numero DDT e lo stock sarà decrementato.')) return;
    try {
      const r = await quotesApi.convertToDdt(quoteId);
      toast.success(`Convertito in DDT ${r.data.number}-${r.data.year}`);
      navigate(`/ddt/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore conversione');
    }
  };

  const handlePrintPdf = async () => {
    try {
      const r = await quotesApi.getPdf(quoteId);
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeTitle = (title || '').replace(/[^a-z0-9\-_]+/gi, '_').slice(0, 40);
      a.download = `Preventivo_${quote?.number}-${quote?.year}${safeTitle ? '_' + safeTitle : ''}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Errore nella generazione PDF');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">Caricamento...</div>;

  return (
    <div data-testid="quote-form-page" className="space-y-4 max-w-6xl mx-auto">
      <ProductSearchDialog open={searchOpen} onOpenChange={setSearchOpen} products={products} onInsert={addProduct} existingItems={items} />

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/preventivi')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight font-['Manrope']">
              {isEdit ? `Preventivo PR-${quote?.number}/${quote?.year}` : 'Nuovo Preventivo'}
            </h1>
            <p className="text-muted-foreground text-sm">
              {readOnly ? 'Preventivo convertito in DDT — sola lettura' : 'Documento pre-vendita (no decremento stock)'}
            </p>
          </div>
        </div>
        {isEdit && (
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handlePrintPdf}
              data-testid="print-quote-pdf-btn"
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
            >
              <Printer className="w-4 h-4 mr-2" /> Stampa PDF
            </Button>
            {!readOnly && (
              <Button type="button" onClick={handleConvert} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="convert-to-ddt-btn">
                <ArrowRightLeft className="w-4 h-4 mr-2" /> Converti in DDT
              </Button>
            )}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Card className="card-swiss">
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-semibold font-['Manrope']">Destinatario</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            <ClientSearch clients={clients} value={selectedClientId} onChange={setSelectedClientId} disabled={readOnly} />
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Nome / Oggetto del preventivo</Label>
              <Input
                data-testid="quote-title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Es. Ordine estivo Bar Centrale — 2026"
                className="h-9 text-sm"
                maxLength={80}
                disabled={readOnly}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Listino</Label>
                <Select value={listino} onValueChange={setListino} disabled={readOnly}>
                  <SelectTrigger data-testid="quote-listino-select" className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Listino 1 (Standard)</SelectItem>
                    <SelectItem value="2">Listino 2 (Ingrosso)</SelectItem>
                    <SelectItem value="3">Listino 3 (Speciale)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Valido fino al</Label>
                <Input type="date" data-testid="quote-valid-until" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className="h-8 text-xs" disabled={readOnly} />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Riferimento interno</Label>
                <Input value={formData.internal_ref} onChange={(e) => setFormData({ ...formData, internal_ref: e.target.value })} className="h-8 text-xs" disabled={readOnly} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-swiss">
          <CardHeader className="py-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-sm font-semibold font-['Manrope']">Prodotti</CardTitle>
              {!readOnly && (
                <Button type="button" onClick={() => setSearchOpen(true)} data-testid="open-product-search-quote-btn" className="h-9 bg-green-600 hover:bg-green-700 text-white">
                  <PackageSearch className="w-4 h-4 mr-2" /> Cerca e Aggiungi Prodotti
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {items.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs font-semibold uppercase h-8 px-2 w-[36px] text-center">#</TableHead>
                      <TableHead className="text-xs font-semibold uppercase h-8 px-2 w-[80px]">Codice</TableHead>
                      <TableHead className="text-xs font-semibold uppercase h-8 px-2">Descrizione</TableHead>
                      <TableHead className="text-xs font-semibold uppercase h-8 px-1 w-[70px] text-center">Box</TableHead>
                      <TableHead className="text-xs font-semibold uppercase h-8 px-1 w-[55px] text-center">Pz/B</TableHead>
                      <TableHead className="text-xs font-semibold uppercase h-8 px-1 w-[70px] text-center">Pezzi</TableHead>
                      <TableHead className="text-xs font-semibold uppercase h-8 px-1 w-[80px] text-right">Prezzo</TableHead>
                      <TableHead className="text-xs font-semibold uppercase h-8 px-2 w-[95px] text-right">Totale</TableHead>
                      <TableHead className="h-8 px-1 w-[32px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item, i) => (
                      <TableRow key={i} className="hover:bg-muted/30">
                        <TableCell className="py-1 px-2 text-sm font-['JetBrains_Mono'] font-semibold text-center text-muted-foreground">{i + 1}</TableCell>
                        <TableCell className="py-1 px-2 text-sm font-['JetBrains_Mono'] font-semibold">{item.product_code}</TableCell>
                        <TableCell className="py-1 px-2">
                          <Input value={item.description} onChange={(e) => updateItem(i, 'description', e.target.value)} className="h-8 text-sm" disabled={readOnly} />
                        </TableCell>
                        <TableCell className="py-1 px-1">
                          <Input type="number" step="any" min="0" value={item.boxes} onChange={(e) => updateItem(i, 'boxes', e.target.value)} className="cell-input h-8 text-center font-['JetBrains_Mono'] text-sm px-1 font-semibold" disabled={readOnly} />
                        </TableCell>
                        <TableCell className="py-1 px-1">
                          <Input type="number" step="1" min="0" value={item.pieces_per_box} onChange={(e) => updateItem(i, 'pieces_per_box', e.target.value)} className="h-8 text-center font-['JetBrains_Mono'] text-sm px-1" disabled={readOnly} />
                        </TableCell>
                        <TableCell className="py-1 px-1">
                          <Input type="number" step="any" min="0" value={item.pieces} onChange={(e) => updateItem(i, 'pieces', e.target.value)} className="cell-input h-8 text-center font-['JetBrains_Mono'] text-sm px-1" disabled={readOnly} />
                        </TableCell>
                        <TableCell className="py-1 px-1">
                          <Input type="number" step="0.01" value={item.price} onChange={(e) => updateItem(i, 'price', e.target.value)} className="h-8 text-right font-['JetBrains_Mono'] text-sm px-1" disabled={readOnly} />
                        </TableCell>
                        <TableCell className="py-1 px-2 text-right font-['JetBrains_Mono'] text-sm font-semibold">{fmt(item.total)}</TableCell>
                        <TableCell className="py-1 px-1 text-center">
                          {!readOnly && (
                            <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeItem(i)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground text-sm">Nessun prodotto. Usa "Cerca e Aggiungi Prodotti" per iniziare.</div>
            )}
          </CardContent>
        </Card>

        <Card className="card-swiss">
          <CardHeader className="py-3"><CardTitle className="text-sm font-semibold font-['Manrope']">Note e info</CardTitle></CardHeader>
          <CardContent className="pt-0 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Causale</Label>
                <Input value={formData.causale} onChange={(e) => setFormData({ ...formData, causale: e.target.value })} className="h-8 text-xs" disabled={readOnly} />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Aspetto beni</Label>
                <Input value={formData.aspetto_beni} onChange={(e) => setFormData({ ...formData, aspetto_beni: e.target.value })} className="h-8 text-xs" disabled={readOnly} />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Note</Label>
              <Textarea value={formData.note} onChange={(e) => setFormData({ ...formData, note: e.target.value })} rows={2} disabled={readOnly} />
            </div>
          </CardContent>
        </Card>

        <div className="sticky bottom-0 -mx-2 px-2 py-3 bg-background/95 backdrop-blur border-t border-border z-10">
          <div className="flex items-center justify-between flex-wrap gap-3 max-w-6xl mx-auto">
            <div className="text-sm">
              <span className="text-muted-foreground mr-3">Totale:</span>
              <span className="font-bold text-lg font-['JetBrains_Mono']">{fmt(totals.total_amount)}</span>
              <span className="text-muted-foreground ml-4">Box: <b>{totals.total_boxes}</b> · Pezzi: <b>{totals.total_pieces}</b></span>
            </div>
            {!readOnly && (
              <Button type="submit" disabled={saving} className="btn-primary" data-testid="save-quote-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Salvataggio...' : (isEdit ? 'AGGIORNA PREVENTIVO' : 'SALVA PREVENTIVO')}
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default QuoteFormPage;
